// Admin: cross-tenant requests list.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter as FilterIcon, Loader2, MapPin, Package, Search } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { ADMIN_NAV } from '../_navs';
import { requests, type RequestStatus, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const STATUS_FILTERS: Array<{ value: 'all' | RequestStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'quote_pending', label: 'Quote pending' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'paid', label: 'Paid' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function AdminRequestsPage() {
  const q = useQuery({ queryKey: ['admin', 'requests'], queryFn: () => requests.list() });
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let list = q.data ?? [];
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        String(r.id).includes(s) ||
        (r.recipient?.full_name ?? '').toLowerCase().includes(s) ||
        (r.title ?? '').toLowerCase().includes(s) ||
        (r.category?.label ?? '').toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }, [q.data, statusFilter, search]);

  return (
    <PortalShell nav={ADMIN_NAV} activeId="requests" title="Requests" subtitle="Every request across all tenants.">
      <div className="space-y-4">
        <Card padding="p-3" className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by id, recipient, category, title…" className="w-full pl-9 pr-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </div>
          <FilterIcon className="w-4 h-4 text-slate-400" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | RequestStatus)} className="text-[12.5px] py-2 px-2 rounded-md border border-slate-200 bg-white">
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Card>

        <Card padding="p-0">
          {q.isLoading ? (
            <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Package} title="No requests" body="Filter cleared everything — try All status." />
          ) : (
            <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-5 font-medium">Request</th>
                  <th className="text-left py-2.5 px-3 font-medium">Customer</th>
                  <th className="text-left py-2.5 px-3 font-medium">Recipient · City</th>
                  <th className="text-left py-2.5 px-3 font-medium">Status</th>
                  <th className="text-right py-2.5 px-5 font-medium">Items · HTG</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => <AdminReqRow key={r.id} r={r} />)}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function AdminReqRow({ r }: { r: ServiceRequestWithItems & { customer?: { id: number; email: string | null } } }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <p className="font-mono text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</p>
        <p className="text-[12px] text-slate-600">{r.category?.label ?? '—'}</p>
      </td>
      <td className="py-3 px-3 text-slate-700">
        <p className="font-mono text-[11.5px]">{r.customer?.email ?? `user #${r.customer_id}`}</p>
      </td>
      <td className="py-3 px-3 text-slate-700">
        <p>{r.recipient?.full_name ?? '—'}</p>
        <p className="text-[11.5px] text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city?.name ?? '—'}</p>
      </td>
      <td className="py-3 px-3"><StatusPill status={r.status} /></td>
      <td className="py-3 px-5 text-right">
        <p className="text-[11.5px] text-slate-500">{r.cart_item_count} items</p>
        <p className="font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : '—'}</p>
      </td>
    </tr>
  );
}
